import { useState, useCallback, useEffect } from 'react';
import { useChat } from '../contexts/ChatContext';
import { CHAT_MESSAGES } from '../constants/chatMessages';

// Define allowed file types for images
export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg', 
  'image/png', 
  'image/gif', 
  'image/webp', 
  'image/avif'
];

// Define allowed file types for documents
export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown'
];

// Combined for backward compatibility
export const ALLOWED_FILE_TYPES = ALLOWED_IMAGE_TYPES;

// Upload timeout - 30 seconds
const UPLOAD_TIMEOUT_MS = 30000;

export interface Attachment {
  key: string;          // This is the attachment ID
  filename: string;
  size: number;
  uploaded: string;
  fileType: string;     // File type (image, document, etc.)
  image_url?: string;   // Optional field for direct image URLs
  download_url?: string; // Optional field for document download URLs
  openai_file_id?: string; // Optional field for OpenAI file reference
  imagePros?: string;   // Positive aspects of the image
  imageCons?: string;   // Concerning aspects of the image
  imageOverall?: string; // Overall assessment of the image
}

interface UseHomeAttachmentsOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  homeIdOverride?: string; // Optional override for homeId from context
}

export function useHomeAttachments(options?: UseHomeAttachmentsOptions) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  
  // Get chat context
  const chatContext = useChat();
  const { sendMessage, refreshMessages } = chatContext;
  
  // Use homeId from context or override
  const homeId = options?.homeIdOverride || chatContext.homeId;

  // Helper to check if a file is an image
  const isImageFile = useCallback((fileType: string) => {
    return fileType === 'image' || fileType.startsWith('image/');
  }, []);

  // Helper to check if a file is a document
  const isDocumentFile = useCallback((fileType: string) => {
    return fileType !== 'image' && !fileType.startsWith('image/');
  }, []);

  // Helper to get attachment URL
  const getAttachmentUrl = useCallback((attachment: Attachment) => {
    // Use direct image_url if available for images
    if ((attachment.fileType === 'image' || attachment.fileType.startsWith('image/')) && attachment.image_url) {
      return attachment.image_url;
    }
    
    // Use download_url if available for documents
    if ((attachment.fileType !== 'image' && !attachment.fileType.startsWith('image/')) && attachment.download_url) {
      return attachment.download_url;
    }
    
    // Fallback to old API endpoint
    return homeId ? `/api/homes/${homeId}/attachment/${attachment.key}` : '';
  }, [homeId]);

  const uploadWithTimeout = useCallback(async (uploadURL: string, formData: FormData): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

    try {
      const response = await fetch(uploadURL, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }, []);

  const uploadWithRetry = useCallback(async (file: File, uploadURL: string, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await uploadWithTimeout(uploadURL, formData);
        
        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }
        
        return; // Success, exit function
      } catch (error) {
        lastError = error;
        // Exponential backoff
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError; // All retries failed
  }, [uploadWithTimeout]);

  // Fetch attachments
  const fetchAttachments = useCallback(async () => {
    if (!homeId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('/api/homes/get/attachments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ home_id: homeId })
      });

      const data = await response.json();
      if (data.success) {
        setAttachments([...data.images]);
      } else {
        setError(data.error || 'Failed to load attachments');
      }
    } catch (err) {
      setError('Failed to fetch attachments');
    } finally {
      setIsLoading(false);
    }
  }, [homeId]);

  // Delete attachment
  const deleteAttachment = useCallback(async (attachmentId: string) => {
    if (!homeId) return false;
    
    try {
      const response = await fetch(`/api/homes/${homeId}/attachment/${attachmentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setAttachments(prev => prev.filter(att => att.key !== attachmentId));
        return true;
      } else {
        setError(data.error || 'Failed to delete attachment');
        return false;
      }
    } catch (err) {
      setError('Failed to delete attachment');
      return false;
    }
  }, [homeId]);

  const uploadFiles = useCallback(async (files: FileList | File[] | null) => {
    if (!homeId || !files || files.length === 0) return false;

    // Filter invalid file types - handle both FileList and File[] types
    const validFiles = Array.isArray(files) 
      ? files.filter(file => ALLOWED_IMAGE_TYPES.includes(file.type))
      : Array.from(files).filter(file => ALLOWED_IMAGE_TYPES.includes(file.type));
    
    if (validFiles.length === 0) {
      const error = 'Please select only image files (JPEG, PNG, GIF, WebP, AVIF)';
      options?.onError?.(error);
      return false;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Get upload URLs for all files
      const response = await fetch(`/api/homes/${homeId}/attachment/generate_image_upload_link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          n: validFiles.length
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URLs: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to get upload URLs');
      }

      // Upload all files with progress tracking
      let completedUploads = 0;
      await Promise.all(validFiles.map((file, index) => 
        uploadWithRetry(file, data.uploadURLs[index].uploadURL).then(() => {
          completedUploads++;
          setUploadProgress(Math.round((completedUploads / validFiles.length) * 100));
        })
      ));

      // After successful uploads, transition to processing state
      setIsUploading(false);
      setIsProcessing(true);

      // After successful uploads, call the sync endpoint
      const syncResponse = await fetch(`/api/homes/${homeId}/attachment/sync`);
      if (!syncResponse.ok) {
        console.warn('Sync operation failed:', await syncResponse.text());
      } else {
        // Refresh messages to reflect the new images
        // TODO: Not 100% sure if this is needed...
        // refreshMessages();
        
        if (sendMessage) {
          // Send a message to the chat about new images
          await sendMessage(CHAT_MESSAGES.REVIEW_NEW_IMAGES);
        }
      }

      const successMsg = `Successfully uploaded ${validFiles.length} files`;
      options?.onSuccess?.(successMsg);
      
      // Refresh the attachments list
      fetchAttachments();
      
      return true;
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Upload failed';
      options?.onError?.(errorMsg);
      return false;
    } finally {
      setIsUploading(false);
      setIsProcessing(false); // Ensure processing is set to false when done or on error
      setUploadProgress(0);
    }
  }, [homeId, uploadWithRetry, sendMessage, options, fetchAttachments, refreshMessages]);

  // Document upload function
  const uploadDocuments = useCallback(async (files: FileList | File[] | null) => {
    if (!homeId || !files || files.length === 0) return false;

    // Filter invalid file types - handle both FileList and File[] types
    const validFiles = Array.isArray(files) 
      ? files.filter(file => ALLOWED_DOCUMENT_TYPES.includes(file.type))
      : Array.from(files).filter(file => ALLOWED_DOCUMENT_TYPES.includes(file.type));
    
    if (validFiles.length === 0) {
      const error = 'Please select only document files (PDF, Word, Text, Markdown)';
      options?.onError?.(error);
      return false;
    }

    try {
      setIsUploadingDocuments(true);
      setDocumentUploadProgress(0);

      // Upload documents using the documents endpoint
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`/api/homes/${homeId}/documents/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Document upload failed with status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Document upload failed');
      }

      setDocumentUploadProgress(100);

      const successMsg = `Successfully uploaded ${validFiles.length} document(s)`;
      options?.onSuccess?.(successMsg);
      
      // Refresh the attachments list to include new documents
      fetchAttachments();
      
      return true;
    } catch (error) {
      console.error('Document upload error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Document upload failed';
      options?.onError?.(errorMsg);
      return false;
    } finally {
      setIsUploadingDocuments(false);
      setDocumentUploadProgress(0);
    }
  }, [homeId, options, fetchAttachments]);

  // Fetch attachments when homeId changes
  useEffect(() => {
    if (homeId) {
      fetchAttachments();
    }
  }, [homeId, fetchAttachments]);

  // New function to get attachment by ID
  const getAttachmentById = useCallback((attachmentId: string): Attachment | undefined => {
    if (!attachmentId) return undefined;
    
    // Convert both to strings to ensure consistent comparison
    const idToFind = String(attachmentId).trim();
    
    // Find attachment with matching key, ensuring string comparison
    const found = attachments.find(attachment => String(attachment.key) === idToFind);
    
    return found;
  }, [attachments]);

  return {
    attachments,
    isLoading,
    isUploading,
    isProcessing,
    isUploadingDocuments,
    error,
    uploadProgress,
    documentUploadProgress,
    uploadFiles,
    uploadDocuments,
    deleteAttachment,
    fetchAttachments,
    getAttachmentUrl,
    isImageFile,
    isDocumentFile,
    ALLOWED_FILE_TYPES,
    ALLOWED_IMAGE_TYPES,
    ALLOWED_DOCUMENT_TYPES,
    homeId,
    getAttachmentById
  };
}